import React, { useState, useEffect } from 'react';
import '@/App.css';
import axios from 'axios';
import { Building2 } from 'lucide-react';
import { API } from './config';
import { useAuth } from './hooks/useAuth';
import LoginScreen from './components/LoginScreen';
import SideNav from './components/SideNav';
import PlantMasterView from './components/PlantMasterView';
import RatesManagementView from './components/RatesManagementView';
import ShiftDataEntryView from './components/ShiftDataEntryView';
import DashboardView from './components/DashboardView';
import UserManagementView from './components/UserManagementView';
import EmailConfigView from './components/EmailConfigView';
import ApprovalsView from './components/ApprovalsView';
import Co2LossView from './components/Co2LossView';
import AuditLogView from './components/AuditLogView';
import ChangePasswordDialog from './components/ChangePasswordDialog';

function App() {
  const auth = useAuth();
  const { isLoggedIn, token, user, loading, setLoading,
    plants, selectedPlant, setSelectedPlant, rates, setRates, permissions,
    loadUserData, loadPlants, loadPlantRates, handleLogin, handleLogout } = auth;

  const [activeView, setActiveView] = useState('dashboard');
  const [showAddPlantForm, setShowAddPlantForm] = useState(false);
  const [newPlant, setNewPlant] = useState({ name: '', location: '', annual_capacity_mt: 0 });
  const [editingPlant, setEditingPlant] = useState(null);

  useEffect(() => {
    // On mount, try to rehydrate session from httpOnly cookie.
    // If no valid cookie, /auth/me returns 401 and login screen stays.
    loadUserData();
  }, [loadUserData]);

  const handlePlantChange = (plantId) => {
    const plant = plants.find(p => p.id === plantId);
    setSelectedPlant(plant);
    loadPlantRates(plantId, token);
  };

  const handleRateChange = (field, value) => {
    setRates(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
  };

  const handleSaveRates = async () => {
    if (!selectedPlant || !rates) return;
    try {
      setLoading(true);
      await axios.put(`${API}/plants/${selectedPlant.id}/rates`, rates, { headers: { Authorization: `Bearer ${token}` } });
      alert('Rates updated successfully!');
    } catch (error) {
      alert('Failed to update rates: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddPlant = async (e) => {
    e.preventDefault();
    if (!newPlant.name.trim()) { alert('Plant name is required'); return; }
    try {
      setLoading(true);
      await axios.post(`${API}/plants`, {
        name: newPlant.name, location: newPlant.location,
        annual_capacity_mt: newPlant.annual_capacity_mt || 0,
        rates: { power_rate_per_unit: 7.0, steam_rate_per_mt: 1500.0, water_rate_per_kl: 50.0,
          cabol_100_rate_per_l: 95.0, freezol_68_rate_per_l: 95.0,
          hydrol_100_rate_per_l: 95.0, hydrol_150_rate_per_l: 95.0, servo_320_rate_per_l: 95.0,
          carbon_refill_rate_per_kg: 45.0, alumina_balls_rate_per_kg: 120.0,
          mol_sieves_13x_rate_per_kg: 0,
          nh3_rate_per_kg: 35.0, kmno4_rate_per_kg: 80.0,
          co2_purchase_rate_per_mt: 0 },
        active: true
      }, { headers: { Authorization: `Bearer ${token}` } });
      alert('Plant added successfully!');
      setNewPlant({ name: '', location: '', annual_capacity_mt: 0 });
      setShowAddPlantForm(false);
      await loadPlants(token);
    } catch (error) {
      alert('Failed to add plant: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePlant = async (plantId) => {
    if (!window.confirm('Are you sure you want to delete this plant?')) return;
    try {
      setLoading(true);
      await axios.delete(`${API}/plants/${plantId}`, { headers: { Authorization: `Bearer ${token}` } });
      alert('Plant deleted successfully!');
      await loadPlants(token);
    } catch (error) {
      alert('Failed to delete plant: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleEditPlant = (plant) => { setEditingPlant(plant); setNewPlant({ name: plant.name, location: plant.location || '', annual_capacity_mt: plant.annual_capacity_mt || 0 }); setShowAddPlantForm(false); };

  const handleUpdatePlant = async (e) => {
    e.preventDefault();
    if (!newPlant.name.trim()) { alert('Plant name is required'); return; }
    try {
      setLoading(true);
      await axios.put(`${API}/plants/${editingPlant.id}`, { name: newPlant.name, location: newPlant.location, annual_capacity_mt: newPlant.annual_capacity_mt || 0 }, { headers: { Authorization: `Bearer ${token}` } });
      alert('Plant updated successfully!');
      setNewPlant({ name: '', location: '', annual_capacity_mt: 0 });
      setEditingPlant(null);
      await loadPlants(token);
    } catch (error) {
      alert('Failed to update plant: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => { setEditingPlant(null); setNewPlant({ name: '', location: '', annual_capacity_mt: 0 }); };

  if (!isLoggedIn) {
    return <LoginScreen onLogin={handleLogin} loading={loading} />;
  }

  const renderContent = () => {
    if (loading && plants.length === 0) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#156ACA]"></div>
        </div>
      );
    }
    if (plants.length === 0) {
      // Admin must always be able to reach Plant Master to create the first plant.
      if (user?.role === 'admin') {
        if (activeView === 'plants') {
          return (
            <PlantMasterView
              plants={plants}
              showAddPlantForm={showAddPlantForm} setShowAddPlantForm={setShowAddPlantForm}
              newPlant={newPlant} setNewPlant={setNewPlant}
              handleAddPlant={handleAddPlant} handleDeletePlant={handleDeletePlant}
              handleEditPlant={handleEditPlant} editingPlant={editingPlant}
              handleUpdatePlant={handleUpdatePlant} handleCancelEdit={handleCancelEdit}
              loading={loading} token={token} onReload={() => loadPlants(token)}
            />
          );
        }
        // For other tabs, prompt admin to create a plant first.
        return (
          <div className="bg-white rounded-lg shadow-md p-12 text-center" data-testid="no-plants-admin-prompt">
            <Building2 className="h-16 w-16 text-[#156ACA] mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-800 mb-2">No Plants Created Yet</h3>
            <p className="text-gray-600 mb-6">Create your first plant to start tracking data, rates, and reports.</p>
            <button
              onClick={() => setActiveView('plants')}
              className="bg-[#156ACA] hover:bg-[#0f56a3] text-white font-semibold px-6 py-3 rounded-lg shadow inline-flex items-center"
              data-testid="go-to-plant-master-btn"
            >
              <Building2 className="h-5 w-5 mr-2" />
              Go to Plant Master
            </button>
          </div>
        );
      }
      return (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <Building2 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No Plants Available</h3>
          <p className="text-gray-600">Contact admin to get plant access.</p>
        </div>
      );
    }
    return (
      <div className="space-y-6">
        {activeView === 'plants' && permissions.plants && (
          <PlantMasterView plants={plants} showAddPlantForm={showAddPlantForm} setShowAddPlantForm={setShowAddPlantForm}
            newPlant={newPlant} setNewPlant={setNewPlant} handleAddPlant={handleAddPlant} handleDeletePlant={handleDeletePlant}
            handleEditPlant={handleEditPlant} editingPlant={editingPlant} handleUpdatePlant={handleUpdatePlant} handleCancelEdit={handleCancelEdit} loading={loading}
            token={token} onReload={() => loadPlants(token)} />
        )}
        {activeView === 'rates' && permissions.rates && (
          <RatesManagementView plants={plants} selectedPlant={selectedPlant}
            handlePlantChange={handlePlantChange} token={token} />
        )}
        {activeView === 'entry' && permissions.entry && <ShiftDataEntryView plants={plants} selectedPlant={selectedPlant} setSelectedPlant={setSelectedPlant} token={token} user={user} />}
        {activeView === 'dashboard' && permissions.dashboard && <DashboardView plants={plants} selectedPlant={selectedPlant} setSelectedPlant={setSelectedPlant} token={token} />}
        {activeView === 'users' && permissions.users && <UserManagementView plants={plants} token={token} />}
        {activeView === 'approvals' && permissions.approvals && <ApprovalsView plants={plants} token={token} />}
        {activeView === 'audit_log' && permissions.audit_log && <AuditLogView plants={plants} token={token} />}
        {activeView === 'co2_losses' && permissions.co2_losses && <Co2LossView plants={plants} selectedPlant={selectedPlant} setSelectedPlant={setSelectedPlant} token={token} />}
        {activeView === 'emails' && permissions.emails && <EmailConfigView plants={plants} selectedPlant={selectedPlant} setSelectedPlant={setSelectedPlant} token={token} />}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#E8F2FC] to-[#EEF9E8]">
      <SideNav user={user} permissions={permissions} activeView={activeView} setActiveView={setActiveView} onLogout={handleLogout} token={token} />
      {/* Main content: on mobile the sidebar is a drawer (no offset), on md+ it offsets by 64px. Top padding on mobile accounts for the fixed mobile top bar (h-14). */}
      <main className="md:ml-16 pt-16 md:pt-0 px-3 sm:px-6 lg:px-8 pb-6 md:py-6">
        {renderContent()}
      </main>
      {/* Force a password change on first login (mandatory, undismissable) */}
      <ChangePasswordDialog
        open={!!user?.must_change_password}
        forced
        token={token}
        onChanged={() => loadUserData(token)}
        onClose={() => {}}
      />
    </div>
  );
}

export default App;
